class AudioManager {
    constructor() {
        this.sounds = {};
        this.volume = 0.8;
        this.isSoundOn = true;
        this.audioContext = null;
        this.isUserInteracted = false;
        this.loadingPromises = {};
    }

    // Initialize audio context safely
    initAudioContext() {
        if (!this.audioContext) {
            try {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                this.audioContext = new AudioContext();
                
                // Handle iOS/mobile autoplay restrictions
                if (this.audioContext.state === 'suspended') {
                    document.addEventListener('click', () => {
                        this.audioContext.resume().then(() => {
                            //console.log('AudioContext resumed successfully');
                        });
                    }, { once: true });
                }
            } catch (error) {
                console.error('Error initializing AudioContext:', error);
            }
        }
        return this.audioContext;
    }

    // Enable sound on first user interaction
    enableSoundOnUserInteraction() {
        if (!this.isUserInteracted) {
            const enableSound = () => {
                this.initAudioContext();
                this.setSoundOn(true);
                this.isUserInteracted = true;
            };
            
            // Add multiple interaction types for better compatibility
            ['click', 'touchstart', 'keydown'].forEach(event => {
                window.addEventListener(event, enableSound, { 
                    once: true,
                    passive: true
                });
            });
        }
    }

    // Set sound on/off with state handling
    setSoundOn(isSoundOn) {
        this.isSoundOn = isSoundOn;
        if (isSoundOn && this.audioContext && this.audioContext.state === 'suspended') {
            this.audioContext.resume().catch(error => {
                console.error('Error resuming audio context:', error);
            });
        }
    }

    // Load sound with caching and error handling
    async loadSound(name, path) {
        if (this.sounds[name]) {
            return Promise.resolve();
        }

        if (!this.loadingPromises[name]) {
            this.loadingPromises[name] = new Promise(async (resolve, reject) => {
                try {
                    const fullPath = `${process.env.PUBLIC_URL}${path}`;
                    const response = await fetch(fullPath);
                    
                    if (!response.ok) {
                        throw new Error(`Failed to fetch ${name}: ${response.status}`);
                    }
                    
                    const arrayBuffer = await response.arrayBuffer();
                    const audioContext = this.initAudioContext();
                    
                    if (!audioContext) {
                        throw new Error('AudioContext not available');
                    }
                    
                    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                    this.sounds[name] = audioBuffer;
                    resolve();
                } catch (error) {
                    console.error(`Error loading sound ${name}:`, error);
                    reject(error);
                } finally {
                    delete this.loadingPromises[name];
                }
            });
        }

        return this.loadingPromises[name];
    }

    // Play sound with safety checks
    playSound(name, volume = this.volume) {
        if (!this.isSoundOn || !this.sounds[name] || !this.audioContext) {
            return;
        }

        try {
            const source = this.audioContext.createBufferSource();
            source.buffer = this.sounds[name];

            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = Math.min(1, Math.max(0, volume));

            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
            source.start(0);
            
            // Clean up after playback ends
            source.addEventListener('ended', () => {
                source.disconnect();
                gainNode.disconnect();
            });
        } catch (error) {
            console.error(`Error playing sound ${name}:`, error);
        }
    }

    // Set global volume with validation
    setVolume(volume) {
        this.volume = Math.min(1, Math.max(0, volume));
    }

    // Preload multiple sounds with progress tracking
    async preloadSounds(soundPaths) {
        const results = await Promise.allSettled(
            soundPaths.map(({ name, path }) => 
                this.loadSound(name, path)
                    .then(() => ({ name, success: true }))
                    .catch(() => ({ name, success: false }))
            )
        );

        const failedLoads = results.filter(r => r.status === 'rejected');
        if (failedLoads.length > 0) {
            console.warn(`${failedLoads.length} sounds failed to load`);
        }
    }
}

// Singleton instance
const audioManager = new AudioManager();
export default audioManager;